import { fetchWithContext } from "../../../utils/fetch";

/**
 * Client and shaping for the ported infrastructure read
 * (GET /api/cluster/{name}/infrastructure), which returns the cloud resources behind a cluster.
 *
 * The response is provider-branched on the server, exactly as V2 branches it: AWS fills account,
 * vpc, eks, rds, elasticache and the subnet set; Azure fills subscription and vnet with a single
 * MAIN subnet; GCP fills project and vpc likewise.
 *
 * The card layout below mirrors V2's Infrastructure tab rather than inventing one, because this is
 * a support screen and the people reading it already know where to look. Two consequences that are
 * easy to get wrong when porting it:
 *
 *   - Fields are NOT dropped when empty. V2 renders a dash, and that is the right call for a
 *     support tool: "we hold no engine version for this cluster" and "this cluster has no engine
 *     version" look identical if the row simply vanishes, and only the first is actionable.
 *     Whole cards are dropped only when the provider does not have that resource at all.
 *   - Azure has no vpcId or vpcCidr; it has vnetName and vnetCidr. V2 shows those under the same
 *     "VPC ID" and "VPC CIDR" labels rather than adding Azure-only rows, so the coalescing happens
 *     here.
 */

export interface EksConfig {
  clusterId?: string;
  version?: string;
  tenancyModel?: string;
  name?: string;
  resourceGroup?: string;
}

export interface ElasticCacheConfig {
  id?: string;
  url?: string;
  replicationGroupId?: string;
  replicationGroupName?: string;
  subnetGroupId?: string;
  nodeType?: string;
  numberCacheClusters?: number;
  port?: string;
  engine?: string;
  engineVersion?: string;
  name?: string;
  tenancyModel?: string;
  cacheConfigType?: string;
}

export interface RdsConfig {
  endpoint?: string;
  engineVersion?: string;
  pgFamily?: string;
  pgName?: string;
  tenancyModel?: string;
}

export interface Subnet {
  awsId?: string;
  type?: string;
}

export interface InfrastructureInfo {
  accountId?: string;
  subscriptionId?: string;
  projectName?: string;
  clusterId?: string;
  cloudProvider?: string;
  region?: string;
  cloudEnvironmentTag?: string;
  azIds?: string[];
  eksConfig?: EksConfig;
  elasticCacheConfig?: ElasticCacheConfig;
  rdsConfig?: RdsConfig;
  subnets?: Subnet[];
  ingressType?: string;
  vpcId?: string;
  vpcCidr?: string;
  vpcName?: string;
  vnetName?: string;
  vnetCidr?: string;
  ipRangeStart?: string;
  ipRangeEnd?: string;
  loadBalancerName?: string;
}

export async function fetchInfrastructure(
  context: object,
  headers: object,
  clusterName: string,
  organizationId?: string
): Promise<InfrastructureInfo> {
  const params = new URLSearchParams();
  if (organizationId) {
    params.set("maybeOrganizationId", organizationId);
  }
  const query = params.toString();
  return fetchWithContext(
    `/cluster/${encodeURIComponent(clusterName)}/infrastructure${
      query ? `?${query}` : ""
    }`,
    context,
    { headers }
  );
}

/** The dash V2 renders for a field it holds no value for. */
export const EMPTY = "-";

export interface InfraField {
  label: string;
  value: string;
}

export type CardIcon = "provider" | "redis" | "postgres" | "network" | "k8s";

export interface InfraCard {
  key: string;
  title: string;
  icon: CardIcon;
  fields: InfraField[];
}

function show(value: unknown): string {
  if (value === undefined || value === null || value === "") {
    return EMPTY;
  }
  return String(value);
}

/** First non-empty of several candidates, so Azure's vnet can sit under the VPC labels. */
function coalesce(...values: unknown[]): string {
  for (const v of values) {
    if (v !== undefined && v !== null && v !== "") {
      return String(v);
    }
  }
  return EMPTY;
}

/** V2 titles the Kubernetes card after the managed service, not generically. */
export function kubernetesTitle(cloudProvider?: string): string {
  switch ((cloudProvider ?? "").toUpperCase()) {
    case "AZURE":
      return "AKS";
    case "GCP":
      return "GKE";
    case "AWS":
      return "EKS";
    default:
      return "Kubernetes";
  }
}

/** Subnets are grouped by type into named fields rather than listed as a table, as V2 does. */
export function subnetsOfType(info: InfrastructureInfo, type: string): string {
  const matching = (info.subnets ?? [])
    .filter((s) => (s.type ?? "").toUpperCase() === type)
    .map((s) => s.awsId ?? "")
    .filter(Boolean)
    .sort();
  return matching.length > 0 ? matching.join(", ") : EMPTY;
}

/**
 * The account, subscription or project the cluster lives in, under the label its provider uses.
 * Only one of the three is ever populated.
 */
function accountField(info: InfrastructureInfo): InfraField {
  switch ((info.cloudProvider ?? "").toUpperCase()) {
    case "AZURE":
      return { label: "Subscription ID", value: show(info.subscriptionId) };
    case "GCP":
      return { label: "Project Name", value: show(info.projectName) };
    default:
      return { label: "Account ID", value: show(info.accountId) };
  }
}

export interface ClusterContext {
  organizationId?: string;
  /** From the cluster, not the infrastructure read: V2's Computing card shows it here. */
  orkesAccount?: boolean;
}

export function toCards(
  info: InfrastructureInfo,
  cluster: ClusterContext = {}
): InfraCard[] {
  const cards: InfraCard[] = [];

  cards.push({
    key: "computing",
    title: "Computing",
    icon: "provider",
    fields: [
      { label: "Cloud Provider", value: show(info.cloudProvider) },
      { label: "Cloud Region", value: show(info.region) },
      { label: "Cloud Env Tag", value: show(info.cloudEnvironmentTag) },
      { label: "Organization ID", value: show(cluster.organizationId) },
      accountField(info),
      {
        label: "Deployment Model",
        value:
          cluster.orkesAccount === undefined
            ? EMPTY
            : cluster.orkesAccount
            ? "Orkes hosted"
            : "Customer hosted",
      },
      { label: "Availability Zones", value: show(info.azIds?.join(", ")) },
    ],
  });

  // Dropped entirely when the provider has no cache, rather than shown as a card of dashes.
  if (info.elasticCacheConfig) {
    const cache = info.elasticCacheConfig;
    cards.push({
      key: "redis",
      title: "Redis",
      icon: "redis",
      fields: [
        { label: "Engine", value: show(cache.engine) },
        { label: "Engine Version", value: show(cache.engineVersion) },
        { label: "Engine Port", value: show(cache.port) },
        { label: "Engine Host", value: show(cache.url) },
        { label: "Replication Group", value: show(cache.replicationGroupName) },
        { label: "Subnet Group ID", value: show(cache.subnetGroupId) },
        { label: "Cache Nodes", value: show(cache.numberCacheClusters) },
        { label: "Node Type", value: show(cache.nodeType) },
        { label: "Tenancy", value: show(cache.tenancyModel) },
      ],
    });
  }

  if (info.rdsConfig) {
    const db = info.rdsConfig;
    cards.push({
      key: "postgres",
      title: "Postgres",
      icon: "postgres",
      fields: [
        { label: "Postgres Family", value: show(db.pgFamily) },
        { label: "Engine Version", value: show(db.engineVersion) },
        { label: "Database Endpoint", value: show(db.endpoint) },
        { label: "Postgres Name", value: show(db.pgName) },
        { label: "Tenancy", value: show(db.tenancyModel) },
      ],
    });
  }

  cards.push({
    key: "networking",
    title: "Networking",
    icon: "network",
    fields: [
      { label: "Ingress Type", value: show(info.ingressType) },
      // Azure fills vnetName/vnetCidr where AWS and GCP fill vpcId/vpcCidr; V2 shows both under
      // the VPC labels rather than adding provider-specific rows.
      { label: "VPC ID", value: coalesce(info.vpcId, info.vnetName) },
      { label: "VPC CIDR", value: coalesce(info.vpcCidr, info.vnetCidr) },
      { label: "IP Range Start", value: show(info.ipRangeStart) },
      { label: "IP Range End", value: show(info.ipRangeEnd) },
      { label: "Main Subnets", value: subnetsOfType(info, "MAIN") },
      { label: "Utility Subnets", value: subnetsOfType(info, "UTILITY") },
      { label: "Load Balancer ID", value: show(info.loadBalancerName) },
    ],
  });

  if (info.eksConfig) {
    const k8s = info.eksConfig;
    const fields: InfraField[] = [
      // V2 shows the cluster's name here rather than its opaque id, falling back when absent.
      { label: "Cluster ID", value: coalesce(k8s.name, k8s.clusterId) },
      { label: "Version", value: show(k8s.version) },
      { label: "Tenancy Model", value: show(k8s.tenancyModel) },
    ];
    if (k8s.resourceGroup) {
      fields.push({ label: "Resource Group", value: k8s.resourceGroup });
    }
    cards.push({
      key: "kubernetes",
      title: kubernetesTitle(info.cloudProvider),
      icon: "k8s",
      fields,
    });
  }

  return cards;
}
